/* UNDONE · Hokusai II landing page
   Loads GSAP + ScrollTrigger (and Lenis when smooth scrolling is on) once, from the URLs on its own <script> tag.
   Every section is optional: each feature checks for its own markup.
   In the theme editor the whole page re-initialises when a section is added, removed, reordered or edited. */
(() => {
'use strict';
if (window.__hokusaiII) return;
window.__hokusaiII = true;
const SELF = document.currentScript;

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const RM = matchMedia('(prefers-reduced-motion: reduce)').matches;
const FINE = matchMedia('(hover: hover) and (pointer: fine)').matches;
const DESIGN = !!(window.Shopify && window.Shopify.designMode);
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const hasGsap = () => !!(window.gsap && window.ScrollTrigger);
document.documentElement.classList.add('hk-js');

/* ───────── pointer = the light (lives for the whole page) ───────── */
const P = { x: innerWidth * .62, y: innerHeight * .42, t: -1e9, moved: false };
addEventListener('pointermove', e => { P.x = e.clientX; P.y = e.clientY; P.t = performance.now(); P.moved = true; }, { passive: true });

let lenis = null;

/* ════════════ WebGL: relight gold leaf from the cursor ════════════ */
const FRAG = `
precision highp float;
varying vec2 vUv;
uniform sampler2D uTex;
uniform vec2 uTexSize, uLight;
uniform float uAspect, uTexAspect, uGoldAll, uBump, uLightH;
uniform vec3 uCam;
vec2 S(){ return uAspect > uTexAspect ? vec2(uAspect, uAspect / uTexAspect) : vec2(uTexAspect, 1.0); }
vec2 mapUV(vec2 p){ vec2 q = p - .5; q.x *= uAspect; return q / S() / uCam.z + uCam.xy; }
float lum(vec2 uv){ return dot(texture2D(uTex, uv).rgb, vec3(.299, .587, .114)); }
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
void main(){
  vec2 uv = mapUV(vUv);
  float inside = step(0., uv.x) * step(0., uv.y) * step(uv.x, 1.) * step(uv.y, 1.);
  vec4 tex = texture2D(uTex, uv) * inside;
  vec3 c = tex.rgb;
  vec2 e = 1.3 / uTexSize;
  float hl = lum(uv - vec2(e.x, 0.)), hr = lum(uv + vec2(e.x, 0.));
  float ht = lum(uv - vec2(0., e.y)), hb = lum(uv + vec2(0., e.y));
  vec3 n = normalize(vec3((hl - hr) * uBump, (ht - hb) * uBump, 1.));
  vec2 d = (mapUV(uLight) - uv) * S() * uCam.z;
  vec3 L = normalize(vec3(d, uLightH));
  vec3 Hv = normalize(L + vec3(0., 0., 1.));
  float diff = max(dot(n, L), 0.);
  float nh = max(dot(n, Hv), 0.);
  float att = 1. / (1. + dot(d, d) * 2.4);
  float warm = c.r - c.b;
  float ratio = c.g / max(c.r, .001);
  float g = uGoldAll > .5 ? 1. : smoothstep(.14, .3, warm) * smoothstep(.6, .7, ratio) * smoothstep(.3, .5, c.r);
  vec3 goldCol = c * (.3 + 1.2 * diff * att)
    + vec3(1., .88, .58) * pow(nh, 44.) * 1.8 * att
    + vec3(1., .82, .5) * pow(nh, 5.) * .2 * att;
  goldCol += vec3(1., .96, .85) * step(.9973, hash(floor(uv * uTexSize * .5))) * pow(nh, 16.) * 2.6 * att;
  vec3 baseCol = c * (.84 + .26 * diff) + vec3(pow(nh, 70.) * .12 * att);
  vec3 col = mix(baseCol, goldCol, g);
  gl_FragColor = vec4(col * tex.a, tex.a);
}`;
const VERT = 'attribute vec2 p;varying vec2 vUv;void main(){vUv=vec2(p.x*.5+.5,.5-p.y*.5);gl_Position=vec4(p,0.,1.);}';

class Relight {
  constructor(canvas, host, o, signal) {
    this.c = canvas; this.host = host;
    this.o = Object.assign({ bump: 6, lightH: .3, goldAll: 0, orbit: [.5, .46, .36, .3] }, o);
    const attrs = { alpha: true, premultipliedAlpha: true, antialias: false };
    const gl = canvas.getContext('webgl2', attrs) || canvas.getContext('webgl', attrs);
    if (!gl || !o.src) throw new Error('webgl');
    this.gl = gl;
    const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; };
    const pr = gl.createProgram();
    gl.attachShader(pr, sh(gl.VERTEX_SHADER, VERT)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(pr);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) throw new Error('link');
    gl.useProgram(pr); this.pr = pr;
    const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    this.u = {}; ['uTex', 'uTexSize', 'uLight', 'uAspect', 'uTexAspect', 'uGoldAll', 'uBump', 'uLightH', 'uCam'].forEach(k => this.u[k] = gl.getUniformLocation(pr, k));
    gl.uniform1i(this.u.uTex, 0);
    this.cam = { x: .5, y: .5, z: 1 }; this.L = { x: .3, y: .3 };
    this.ready = false; this.visible = false; this.dead = false; this.raf = 0; this.touchT = -1e9;
    this.tick = this.tick.bind(this);
    this.loadTex(o.src, 0).then(img => {
      this.texSize = [img.naturalWidth, img.naturalHeight];
    }).then(() => { if (this.dead) return; this.ready = true; this.resize(); host.classList.add('hk-gl'); this.kick(); })
      .catch(() => { canvas.style.display = 'none'; });
    this.ro = new ResizeObserver(() => this.resize()); this.ro.observe(canvas);
    this.io = new IntersectionObserver(es => { this.visible = es[0].isIntersecting; this.kick(); }, { rootMargin: '120px' }); this.io.observe(canvas);
    const setFromEvent = e => { this.touchT = performance.now(); this.px = e.clientX; this.py = e.clientY; };
    host.addEventListener('pointermove', setFromEvent, { passive: true, signal });
    host.addEventListener('pointerdown', setFromEvent, { passive: true, signal });
  }
  loadTex(src, unit) {
    return new Promise((res, rej) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (this.dead) { rej(new Error('dead')); return; }
        const gl = this.gl; const t = gl.createTexture();
        gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(gl.TEXTURE_2D, t);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img); } catch (err) { rej(err); return; }
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        res(img);
      };
      img.onerror = rej; img.src = src;
    });
  }
  resize() {
    if (this.dead) return;
    const dpr = Math.min(devicePixelRatio || 1, 2);
    const r = this.c.getBoundingClientRect();
    const w = Math.max(2, Math.round(r.width * dpr)), h = Math.max(2, Math.round(r.height * dpr));
    if (this.c.width !== w || this.c.height !== h) { this.c.width = w; this.c.height = h; }
    this.aspect = r.width / Math.max(1, r.height);
    if (this.ready) this.draw();
  }
  kick() { if (this.ready && this.visible && !this.raf && !this.dead) this.raf = requestAnimationFrame(this.tick); }
  tick(now) {
    this.raf = 0;
    if (!this.visible || this.dead) return;
    let tx, ty;
    const k = now / 1000, [ox, oy, ax, ay] = this.o.orbit;
    const r = this.c.getBoundingClientRect();
    const fresh = now - this.touchT < 3200 || (FINE && now - P.t < 3200 && P.y > r.top - r.height * .6 && P.y < r.bottom + r.height * .6);
    if (fresh) {
      const px = now - this.touchT < 3200 ? this.px : P.x, py = now - this.touchT < 3200 ? this.py : P.y;
      tx = clamp((px - r.left) / r.width, -.6, 1.6); ty = clamp((py - r.top) / r.height, -.6, 1.6);
      if (this.onLight) this.onLight();
    } else { tx = ox + ax * Math.cos(k * .34); ty = oy + ay * Math.sin(k * .5); }
    this.L.x += (tx - this.L.x) * .1; this.L.y += (ty - this.L.y) * .1;
    this.draw();
    if (!RM || fresh) this.raf = requestAnimationFrame(this.tick);
  }
  draw() {
    if (this.dead || !this.ready) return;
    const gl = this.gl, u = this.u;
    gl.viewport(0, 0, this.c.width, this.c.height);
    gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform2f(u.uTexSize, this.texSize[0], this.texSize[1]);
    gl.uniform2f(u.uLight, this.L.x, this.L.y);
    gl.uniform1f(u.uAspect, this.aspect);
    gl.uniform1f(u.uTexAspect, this.texSize[0] / this.texSize[1]);
    gl.uniform1f(u.uGoldAll, this.o.goldAll);
    gl.uniform1f(u.uBump, this.o.bump);
    gl.uniform1f(u.uLightH, this.o.lightH);
    gl.uniform3f(u.uCam, this.cam.x, this.cam.y, this.cam.z);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
  destroy() {
    this.dead = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.ro.disconnect(); this.io.disconnect();
  }
}

/* ───────── split headings into words (once per element) ───────── */
function splitWords(el, cls = 'hk-w') {
  if (el.dataset.hkSplit) return $$('.' + cls + 'i', el);
  el.dataset.hkSplit = '1';
  const out = [];
  const walk = node => {
    [...node.childNodes].forEach(n => {
      if (n.nodeType === 3) {
        const parts = n.textContent.split(/(\s+)/);
        const frag = document.createDocumentFragment();
        parts.forEach(p => {
          if (!p) return;
          if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(' ')); return; }
          const w = document.createElement('span'); w.className = cls;
          const i = document.createElement('span'); i.className = cls + 'i'; i.textContent = p;
          w.appendChild(i); frag.appendChild(w); out.push(i);
        });
        n.replaceWith(frag);
      } else if (n.nodeType === 1 && !n.classList.contains('hk-sr-only') && n.tagName !== 'BR') walk(n);
    });
  };
  walk(el);
  return out;
}

/* ───────── sign-up drawer (delegated, so it survives editor re-renders) ───────── */
let lastFocus = null;
const drawerEl = () => $('#hk-drawer');
function openDrawer() {
  const drawer = drawerEl();
  if (!drawer) {
    const field = $('#hk-f-email');
    if (field) {
      const host = field.closest('section') || field;
      lenis ? lenis.scrollTo(host, { duration: 1.6 }) : host.scrollIntoView({ behavior: RM ? 'auto' : 'smooth' });
      setTimeout(() => field.focus({ preventScroll: true }), RM ? 0 : 1200);
    }
    return;
  }
  lastFocus = document.activeElement;
  drawer.hidden = false;
  document.documentElement.classList.add('hk-drawer-open');
  requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.add('hk-open')));
  lenis && lenis.stop();
  document.documentElement.style.overflow = 'hidden';
  setTimeout(() => {
    const body = $('#hk-drBody', drawer);
    const f = body && body.hidden ? $('.hk-done .hk-btn', drawer) : $('#hk-dr-email', drawer);
    f && f.focus({ preventScroll: true });
  }, 380);
}
function closeDrawer() {
  const drawer = drawerEl();
  if (!drawer || drawer.hidden) return;
  drawer.classList.remove('hk-open');
  document.documentElement.classList.remove('hk-drawer-open');
  lenis && lenis.start();
  document.documentElement.style.overflow = '';
  setTimeout(() => { drawer.hidden = true; }, 820);
  lastFocus && lastFocus.focus && lastFocus.focus({ preventScroll: true });
}
document.addEventListener('click', e => {
  if (e.target.closest('[data-signup]')) { e.preventDefault(); openDrawer(); return; }
  if (e.target.closest('#hk-drawer [data-close]')) { closeDrawer(); return; }
  const a = e.target.closest('.hk2 a[href^="#"]');
  if (a) {
    const id = a.getAttribute('href').slice(1);
    const el = id && document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    lenis ? lenis.scrollTo(el, { duration: 1.6 }) : el.scrollIntoView({ behavior: RM ? 'auto' : 'smooth' });
  }
});
document.addEventListener('keydown', e => {
  const drawer = drawerEl();
  if (!drawer || drawer.hidden) return;
  if (e.key === 'Escape') { closeDrawer(); return; }
  if (e.key !== 'Tab') return;
  const panel = $('.hk-panel', drawer);
  const f = $$('button,input,a[href]', panel).filter(el => !el.closest('[hidden]') && el.offsetParent !== null);
  if (!f.length) return;
  const first = f[0], last = f[f.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
});

/* ───────── sign-up forms: Shopify customer form by default, or an external endpoint ───────── */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MSG = {
  empty: 'Enter your email to join the list.',
  invalid: 'That email looks incomplete. Check it and try again.',
  busy: 'Adding you to the list…',
  ok: 'You’re on the list. We’ll email you the moment Hokusai II opens.',
  fail: 'We couldn’t add you just now. Check your connection and try again.'
};
document.addEventListener('change', e => {
  const r = e.target.closest('.hk-signup-form input[name="strap"]');
  if (!r) return;
  const form = r.form, host = form.closest('.hk-form-host'), tags = $('input[name="contact[tags]"]', form);
  if (tags && host) tags.value = [host.dataset.tags, 'strap-' + r.value].filter(Boolean).join(', ');
});
/* Klaviyo: browser-side subscribe with the public company ID (no private key in the theme).
   https://developers.klaviyo.com/en/reference/create_client_subscription */
const KLAVIYO_REVISION = '2026-07-15';
async function klaviyoSubscribe(cfg, { email, firstName, strap, formName }) {
  const properties = { hokusai_ii_form: formName };
  if (strap) properties.hokusai_ii_strap = strap;
  const attributes = { email, properties, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } };
  if (firstName) attributes.first_name = firstName;
  const res = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(cfg.klaviyoCompany)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
    body: JSON.stringify({ data: { type: 'subscription',
      attributes: { custom_source: cfg.klaviyoSource || 'Hokusai II launch page', profile: { data: { type: 'profile', attributes } } },
      relationships: { list: { data: { type: 'list', id: cfg.klaviyoList } } } } })
  });
  if (!res.ok) throw new Error('klaviyo ' + res.status);
  try { if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email }); } catch (err) {}
}
async function shopifySubscribe(form) {
  const res = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
  return { challenge: /\/challenge/.test(res.url), ok: res.ok };
}

/* Meta Pixel Lead + a Shopify custom event ("hokusai_ii_lead") for Customer events pixels */
const marketingAllowed = () => {
  const cp = window.Shopify && window.Shopify.customerPrivacy;
  try { if (cp && typeof cp.marketingAllowed === 'function') return cp.marketingAllowed(); } catch (err) {}
  return true;
};
const loadPixel = id => {
  if (typeof window.fbq !== 'function') {
    /* Meta's standard loader, without the automatic PageView: this page only reports the Lead */
    !function (f, b, e, v, n, t, s) { if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); }; if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = []; t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s); }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  }
  if (!loadPixel.inited) { window.fbq('init', id); loadPixel.inited = true; }
};
function trackLead(cfg, formName) {
  const eventID = 'hk2-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  const data = { content_name: cfg.leadName || 'Hokusai II launch list', content_category: 'Newsletter sign-up', hokusai_form: formName };
  try { if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish('hokusai_ii_lead', Object.assign({ event_id: eventID }, data)); } catch (err) {}
  if (cfg.pixelLead === 'false') return;
  try {
    if (typeof window.fbq === 'function' && !cfg.pixelId) { window.fbq('track', 'Lead', data, { eventID }); return; }
    if (cfg.pixelId && marketingAllowed()) {
      loadPixel(cfg.pixelId);
      window.fbq('trackSingle', cfg.pixelId, 'Lead', data, { eventID });
    }
  } catch (err) {}
}

document.addEventListener('submit', async e => {
  const form = e.target.closest('.hk-signup-form');
  if (!form) return;
  e.preventDefault();
  const host = form.closest('.hk-form-host') || form;
  const input = $('input[type=email]', form), status = $('.hk-status', form), btn = $('button[type=submit]', form);
  const email = input.value.trim();
  status.className = 'hk-status';
  if (!EMAIL.test(email)) {
    input.setAttribute('aria-invalid', 'true');
    status.classList.add('hk-err');
    status.textContent = email ? MSG.invalid : MSG.empty;
    input.focus(); return;
  }
  input.removeAttribute('aria-invalid');
  btn.disabled = true; status.textContent = MSG.busy;
  const cfgEl = $('[data-hk-config]');
  const cfg = cfgEl ? cfgEl.dataset : {};
  const formName = form.id === 'hk-drForm' ? 'drawer' : 'page';
  try {
    const ep = host.dataset.endpoint;
    const useKlaviyo = !!(cfg.klaviyoCompany && cfg.klaviyoList) && cfg.signupTo !== 'shopify';
    const useShopify = !useKlaviyo || cfg.signupTo === 'both';
    if (ep) {
      const res = await fetch(ep, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(res.status);
    } else if (useKlaviyo) {
      const strapEl = $('input[name="strap"]:checked', form), nameEl = $('input[name="contact[first_name]"]', form);
      const jobs = [klaviyoSubscribe(cfg, { email, firstName: nameEl ? nameEl.value.trim() : '', strap: strapEl ? strapEl.value : '', formName })];
      /* "both": the Shopify customer is a bonus copy, so its result never blocks the Klaviyo sign-up */
      if (useShopify) jobs.push(shopifySubscribe(form).catch(() => null));
      await jobs[0];
      await Promise.allSettled(jobs.slice(1));
    } else {
      const r = await shopifySubscribe(form);
      /* Shopify bot protection: hand over to a normal submit so the shopper can pass the challenge */
      if (r.challenge) { form.submit(); return; }
      if (!r.ok) throw new Error('shopify');
    }
    trackLead(cfg, formName);
    if (form.id === 'hk-drForm') {
      const mail = $('#hk-drMail'), body = $('#hk-drBody'), done = $('#hk-drDone');
      if (mail) mail.textContent = email;
      if (body && done) { body.hidden = true; done.hidden = false; }
      if (hasGsap() && !RM) gsap.from('.hk-stamp-ok', { scale: 2.4, rotate: -28, opacity: 0, duration: .55, ease: 'power4.in' });
      const back = $('.hk-done .hk-btn'); back && back.focus({ preventScroll: true });
    } else {
      status.classList.add('hk-ok');
      status.textContent = host.dataset.success || MSG.ok;
      form.reset();
    }
  } catch (err) {
    status.classList.add('hk-err');
    status.textContent = MSG.fail;
  } finally { btn.disabled = false; }
});

/* ════════════ everything tied to the current DOM; torn down and rebuilt in the theme editor ════════════ */
function boot() {
  const ac = new AbortController(), signal = ac.signal;
  const on = (t, ev, fn, opt = {}) => t && t.addEventListener(ev, fn, Object.assign({ signal }, opt));
  let alive = true;
  const observers = [], gls = [];
  const watch = o => (observers.push(o), o);
  const G = hasGsap();
  if (G) gsap.registerPlugin(ScrollTrigger);
  const cfg = $('[data-hk-config]');
  const navMode = cfg ? cfg.dataset.navMode : 'reveal';

  if (G && !RM && !DESIGN && window.Lenis && (!cfg || cfg.dataset.smooth !== 'false')) {
    lenis = new Lenis({ lerp: .085, smoothWheel: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(lenis.__hkRaf = t => lenis && lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
  }

  let mm = null;
  const run = () => {
    /* ───────── cursor ───────── */
    const cur = $('.hk-cursor');
    if (FINE && cur) {
      const ring = $('.hk-c-ring', cur), dot = $('.hk-c-dot', cur), lbl = $('span', ring);
      const base = lbl.textContent;
      let rx = P.x, ry = P.y;
      const loop = () => {
        if (!alive) return;
        rx += (P.x - rx) * .2; ry += (P.y - ry) * .2;
        dot.style.transform = `translate(${P.x}px,${P.y}px)`;
        ring.style.transform = `translate(${rx}px,${ry}px)`;
        requestAnimationFrame(loop);
      };
      loop();
      on(document, 'pointerover', e => {
        const t = e.target.closest('.hk2 [data-cursor]');
        const mode = t ? t.dataset.cursor : '';
        cur.classList.toggle('hk-lamp', mode === 'lamp');
        cur.classList.toggle('hk-big', !!mode && mode !== 'lamp');
        lbl.textContent = mode && mode !== 'lamp' ? mode : base;
        cur.classList.toggle('hk-hide', !e.target.closest('.hk2') || !!e.target.closest('input,.hk-drawer'));
      });
      on(document.documentElement, 'pointerleave', () => cur.classList.add('hk-hide'));
      on(document.documentElement, 'pointerenter', () => cur.classList.remove('hk-hide'));
    } else if (cur) cur.style.display = 'none';

    /* ───────── gold type: each gold word tracks the light ───────── */
    const hero = $('.hk-hero');
    const golds = $$('.hk-gold-type').map(el => ({ el, lx: 30, ly: 40, vis: false }));
    const H = { lx: innerWidth * .6, ly: innerHeight * .4, vis: !!hero };
    const vio = watch(new IntersectionObserver(es => es.forEach(e => {
      const g = golds.find(g => g.el === e.target); if (g) g.vis = e.isIntersecting;
      if (e.target === hero) H.vis = e.isIntersecting;
    })));
    golds.forEach(g => vio.observe(g.el)); hero && vio.observe(hero);
    const T0 = performance.now();
    const lightLoop = now => {
      if (!alive) return;
      const k = (now - T0) / 1000;
      const idle = !P.moved || now - P.t > 3500;
      for (const g of golds) {
        if (!g.vis) continue;
        const r = g.el.getBoundingClientRect();
        let tx, ty;
        if (idle) { tx = 50 + 44 * Math.sin(k * .42); ty = 46 + 34 * Math.sin(k * .67 + 1.2); }
        else { tx = (P.x - r.left) / r.width * 100; ty = (P.y - r.top) / r.height * 100; }
        g.lx += (tx - g.lx) * .085; g.ly += (ty - g.ly) * .085;
        g.el.style.setProperty('--lx', g.lx.toFixed(2) + '%');
        g.el.style.setProperty('--ly', g.ly.toFixed(2) + '%');
      }
      if (hero && H.vis) {
        const r = hero.getBoundingClientRect();
        let tx, ty;
        if (idle) { tx = r.width * (.5 + .3 * Math.sin(k * .42)); ty = r.height * (.44 + .2 * Math.sin(k * .67 + 1.2)); }
        else { tx = P.x - r.left; ty = P.y - r.top; }
        H.lx += (tx - H.lx) * .08; H.ly += (ty - H.ly) * .08;
        hero.style.setProperty('--lx', H.lx.toFixed(1) + 'px');
        hero.style.setProperty('--ly', H.ly.toFixed(1) + 'px');
      }
      if (!RM) requestAnimationFrame(lightLoop);
    };
    requestAnimationFrame(lightLoop);

    /* ───────── hero wordmark fits its line (key block and gold block share one size) ───────── */
    const mark = $('#hk-heroMark'), markWrap = $('.hk-hero-mark'), seal = $('.hk-hero-seal');
    const heroName = $('.hk-hero-name'), heroFig = $('#hk-heroWatch');
    function fitName() {
      if (!heroName || !mark) return;
      const a = $('.hk-hn-a', heroName), b = $('.hk-hn-b', heroName);
      heroName.style.fontSize = '100px';
      const wa = a.offsetWidth || 1, wb = b.offsetWidth || 1;
      const markPx = parseFloat(getComputedStyle(mark).fontSize);
      let px;
      if (innerWidth <= 820 || !heroFig) {
        px = 100 * mark.offsetWidth / Math.max(wa, wb);
      } else {
        const f = heroFig.getBoundingClientRect(), m = mark.getBoundingClientRect();
        const gap = Math.max(20, innerWidth * .018);
        const caseL = f.left + f.width * .094, caseR = f.right - f.width * .137;
        px = 100 * Math.min((caseL - m.left - gap) / wa, (m.right - caseR - gap) / wb);
      }
      heroName.style.fontSize = Math.max(14, Math.min(px, markPx * .36)).toFixed(1) + 'px';
    }
    function fitMark() {
      if (!mark || !hero) return;
      markWrap.style.setProperty('--mark', '100px');
      const w = mark.offsetWidth || 1;
      const cs = getComputedStyle(hero);
      const avail = hero.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - (seal ? seal.offsetWidth : 0) - 10 - (innerWidth > 1000 ? 56 : 0);
      const cap = innerWidth > 820 ? innerHeight * .34 : 999;
      markWrap.style.setProperty('--mark', Math.max(40, Math.min(100 * avail / w, cap)).toFixed(1) + 'px');
      fitName();
    }
    fitMark();
    on(window, 'resize', fitMark);

    /* ───────── top bar: solid once you leave the top, out of the way on the way down, paper over paper ───────── */
    const nav = $('.hk-nav'), papers = $$('.hk-paper');
    if (nav) {
      const first = $('.hk2-section');
      let startY = 0;
      const measure = () => { startY = navMode === 'reveal' && first ? first.getBoundingClientRect().top + scrollY : 0; };
      /* whatever the theme keeps pinned at the top of the viewport (sticky header, announcement bar…) */
      const topCover = () => {
        let bottom = 0;
        for (const el of document.elementsFromPoint(innerWidth / 2, 2)) {
          if (el.closest('.hk2') || el === document.body || el === document.documentElement) continue;
          for (let n = el; n && n !== document.body; n = n.parentElement) {
            const pos = getComputedStyle(n).position;
            if (pos === 'fixed' || pos === 'sticky') { const r = n.getBoundingClientRect(); if (r.top <= 2 && r.bottom > bottom) bottom = r.bottom; break; }
          }
        }
        return Math.max(0, Math.round(bottom));
      };
      let cover = -1;
      const setCover = () => { const c = navMode === 'reveal' ? topCover() : 0; if (c !== cover) { cover = c; nav.style.setProperty('--hk-cover', c + 'px'); } };
      measure();
      let navY = scrollY, queued = false;
      const navUpdate = () => {
        queued = false;
        setCover();
        const y = scrollY, dy = y - navY; navY = y;
        if (navMode === 'reveal') {
          nav.classList.add('hk-solid');
          if (y < startY + 4) nav.classList.add('hk-hid');
          else if (Math.abs(dy) > 3) nav.classList.toggle('hk-hid', dy > 0 && y > startY + innerHeight * .7);
        } else {
          nav.classList.toggle('hk-solid', y > 40);
          if (Math.abs(dy) > 3) nav.classList.toggle('hk-hid', dy > 0 && y > innerHeight * .7);
        }
        const probe = cover + nav.offsetHeight / 2;
        nav.classList.toggle('hk-on-paper', papers.some(p => { const r = p.getBoundingClientRect(); return r.top <= probe && r.bottom > probe; }));
      };
      on(window, 'scroll', () => { if (!queued) { queued = true; requestAnimationFrame(navUpdate); } }, { passive: true });
      on(window, 'resize', measure);
      on(window, 'load', measure);
      on(nav, 'focusin', () => nav.classList.remove('hk-hid'));
      navUpdate();
    }

    /* ───────── proofs carousel dots (the carousel itself is CSS scroll-snap, phones only) ───────── */
    const pRow = $('.hk-proof-row'), pDots = $$('.hk-proof-dot');
    if (pRow && pDots.length) {
      const sheets = $$('.hk-proof', pRow);
      const active = () => {
        const mid = pRow.scrollLeft + pRow.clientWidth / 2;
        let best = 0, d = Infinity;
        sheets.forEach((s, i) => { const c = s.offsetLeft + s.offsetWidth / 2, x = Math.abs(c - mid); if (x < d) { d = x; best = i; } });
        pDots.forEach((b, i) => b.setAttribute('aria-current', String(i === best)));
      };
      let pq = false;
      on(pRow, 'scroll', () => { if (!pq) { pq = true; requestAnimationFrame(() => { pq = false; active(); }); } }, { passive: true });
      pDots.forEach((b, i) => on(b, 'click', () => {
        const s = sheets[i]; if (!s) return;
        pRow.scrollTo({ left: s.offsetLeft - (pRow.clientWidth - s.offsetWidth) / 2, behavior: RM ? 'auto' : 'smooth' });
      }));
      on(window, 'resize', active);
      active();
    }

    /* ───────── kento marks snap into register when seen ───────── */
    const kio = watch(new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('hk-in'); kio.unobserve(e.target); } }), { threshold: .35 }));
    $$('.hk-reg').forEach(el => kio.observe(el));

    /* ───────── the dial + the gold leaf, relit in WebGL ───────── */
    const dialStage = $('#hk-dialStage'), leafSec = $('.hk-leaf');
    let dialGL = null, leafGL = null;
    try { if (dialStage) gls.push(dialGL = new Relight($('#hk-dialGL'), dialStage, { src: dialStage.dataset.tex, bump: 5.5, lightH: .32, orbit: [.5, .46, .4, .34] }, signal)); } catch (e) { dialGL = null; }
    try { if (leafSec) gls.push(leafGL = new Relight($('#hk-leafGL'), leafSec, { src: leafSec.dataset.tex, bump: 9, lightH: .42, goldAll: 1, orbit: [.55, .5, .35, .3] }, signal)); } catch (e) { leafGL = null; }
    const dialHint = $('#hk-dialHint'), leafHint = $('#hk-leafHint');
    if (dialGL) dialGL.onLight = () => dialHint && dialHint.classList.add('hk-gone');
    if (leafGL) leafGL.onLight = () => leafHint && leafHint.classList.add('hk-gone');

    /* lights out: the lume plate is a plain <img> crossfaded over the dial in CSS (.hk-night),
       so iOS never has to hold a second WebGL texture */
    const lumeBtn = $('#hk-lumeBtn'), dialSec = $('.hk-dial'), lumeImg = $('.hk-dial-lume');
    on(lumeBtn, 'pointerenter', () => { if (lumeImg) lumeImg.loading = 'eager'; });
    on(lumeBtn, 'click', () => {
      const lit = lumeBtn.getAttribute('aria-pressed') !== 'true';
      lumeBtn.setAttribute('aria-pressed', lit);
      const lbl = $('#hk-lumeLbl'); if (lbl) lbl.textContent = lit ? lumeBtn.dataset.labelOn : lumeBtn.dataset.labelOff;
      if (lumeImg) lumeImg.loading = 'eager';
      dialSec && dialSec.classList.toggle('hk-night', lit);
    });
    if (lumeImg && dialStage) watch(new IntersectionObserver((es, o) => { if (es[0].isIntersecting) { lumeImg.loading = 'eager'; o.disconnect(); } }, { rootMargin: '600px 0px' })).observe(dialStage);

    /* ───────── straps: hover is CSS, touch gets a tap ───────── */
    const looks = $$('.hk-look');
    if (!FINE) looks.forEach(l => on(l, 'click', e => {
      if (e.target.closest('button,a')) return;
      looks.forEach(x => x.classList.toggle('hk-on', x === l && !l.classList.contains('hk-on')));
    }));
    const lookPs = $$('.hk-look-meta p');
    const alignLooks = () => {
      lookPs.forEach(p => p.style.minHeight = '');
      if (innerWidth <= 900 || !lookPs.length) return;
      const h = Math.max(...lookPs.map(p => p.offsetHeight));
      lookPs.forEach(p => p.style.minHeight = h + 'px');
    };
    alignLooks();
    on(window, 'resize', alignLooks);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => alive && alignLooks());
    const split2 = $('.hk-split2');
    on(split2, 'transitionend', e => { if (e.target === split2) alignLooks(); });
    looks.forEach(l => {
      on(l, 'focus', () => looks.forEach(x => x.classList.toggle('hk-on', x === l)));
      on(l, 'blur', () => l.classList.remove('hk-on'));
    });

    /* ───────── the set: each card turns over to show the piece; a first peek teaches the gesture ───────── */
    const tilesEl = $('.hk-tiles');
    if (tilesEl) {
      const TW = !!window.gsap;
      if (TW) tilesEl.classList.add('hk-js-flip');
      const cards = $$('.hk-tile', tilesEl).map(tile => ({ tile, flip: $('.hk-flip', tile), on: tile.classList.contains('hk-on'), hover: false, turning: false,
        shades: $$('.hk-face', tile).map(f => $('.hk-shade', f) || f.appendChild(Object.assign(document.createElement('span'), { className: 'hk-shade' }))) }));
      const angle = c => (c.on ? 180 : 0) - (c.hover ? 8 : 0);
      const shade = c => {
        const a = gsap.getProperty(c.flip, 'rotationY') * Math.PI / 180;
        const o = ((1 - Math.abs(Math.cos(a))) * .6).toFixed(3);
        c.shades.forEach(s => { s.style.opacity = o; });
      };
      const settle = (c, d = .65) => gsap.to(c.flip, { rotationY: angle(c), duration: d, ease: 'power2.out', overwrite: 'auto', onUpdate: () => shade(c) });
      const turn = c => {
        c.on = !c.on;
        c.flip.setAttribute('aria-pressed', String(c.on));
        c.tile.classList.toggle('hk-on', c.on);
        if (!TW) return;
        if (RM) { gsap.set(c.flip, { rotationY: angle(c) }); return; }
        c.turning = true;
        gsap.to(c.flip, { rotationY: angle(c), duration: .9, ease: 'power3.inOut', overwrite: 'auto', onUpdate: () => shade(c),
          onComplete: () => { c.turning = false; if (gsap.getProperty(c.flip, 'rotationY') !== angle(c)) settle(c, .5); } });
        gsap.to(c.flip, { keyframes: [{ scale: .955, duration: .42, ease: 'sine.out' }, { scale: 1, duration: .48, ease: 'sine.inOut' }], overwrite: 'auto' });
      };
      cards.forEach(c => {
        if (TW && c.on) gsap.set(c.flip, { rotationY: 180 });
        on(c.flip, 'click', () => turn(c));
        if (TW && !RM) {
          const hov = v => e => { if (e.pointerType !== 'mouse') return; c.hover = v; if (!c.turning) settle(c); };
          on(c.flip, 'pointerenter', hov(true));
          on(c.flip, 'pointerleave', hov(false));
        }
      });
      if (DESIGN) on(document, 'shopify:block:select', e => {
        const c = cards.find(c => c.tile === e.target || c.tile.contains(e.target));
        if (c && !c.on) turn(c);
      });
      const dio = watch(new IntersectionObserver(es => {
        if (!es.some(e => e.isIntersecting)) return;
        $$('img', tilesEl).forEach(img => { img.loading = 'eager'; img.decode && img.decode().catch(() => {}); });
        dio.disconnect();
      }, { rootMargin: '900px 0px' }));
      dio.observe(tilesEl);
      if (!RM) {
        const pio = watch(new IntersectionObserver(es => es.forEach(e => {
          if (!e.isIntersecting) return;
          pio.disconnect();
          if (!TW) {
            setTimeout(() => tilesEl.classList.add('hk-peek'), 900);
            setTimeout(() => tilesEl.classList.remove('hk-peek'), 900 + 1600);
            return;
          }
          cards.forEach((c, i) => {
            if (c.on) return;
            gsap.to(c.flip, { rotationY: -26, duration: .46, ease: 'sine.inOut', yoyo: true, repeat: 1, delay: .9 + i * .12,
              overwrite: 'auto', onUpdate: () => shade(c) });
          });
        }), { threshold: .45 }));
        pio.observe(tilesEl);
      }
    }

    /* ───────── caseback: the printed glass lifts highest, the rotor floats between ───────── */
    const explode = $('#hk-explode'), rotor = $('#hk-rotor');
    const lCase = $('.hk-l-case'), lRotor = $('.hk-l-rotor'), lGlass = $('.hk-l-glass');
    const X = { rx: 0, rz: 0, s: 0 };
    const renderExplode = () => {
      if (!explode) return;
      const W = explode.offsetWidth;
      explode.style.transform = `rotateX(${X.rx}deg) rotateZ(${X.rz}deg)`;
      lCase.style.transform = 'translateZ(0px)';
      lRotor.style.transform = `translateZ(${(X.s * W * .24).toFixed(1)}px)`;
      lGlass.style.transform = `translateZ(${(X.s * W * .5).toFixed(1)}px)`;
      lRotor.style.opacity = Math.min(1, X.s * 4).toFixed(3);
      lGlass.style.opacity = Math.min(1, X.s * 3).toFixed(3);
    };
    renderExplode();
    on(window, 'resize', renderExplode);
    if (rotor) {
      let rotA = 0, lastY = scrollY, rotVis = false;
      watch(new IntersectionObserver(es => { rotVis = es[0].isIntersecting; })).observe(rotor);
      const spin = () => {
        if (!alive) return;
        const y = scrollY, v = Math.abs(y - lastY); lastY = y;
        if (rotVis) { rotA += (RM ? 0 : .12) + Math.min(v, 120) * .5; rotor.style.transform = `rotate(${rotA.toFixed(2)}deg)`; }
        requestAnimationFrame(spin);
      };
      requestAnimationFrame(spin);
    }
    const edNum = $('#hk-edNum');
    /* the edition number counts up from 001 to the number set in the editor (e.g. 200) */
    const edTarget = edNum ? (edNum.dataset.target || edNum.textContent).trim() : '';
    const edEnd = /^\d+$/.test(edTarget) ? parseInt(edTarget, 10) : 0;
    const edPad = Math.max(3, edTarget.length);
    const edFmt = n => String(n).padStart(edPad, '0');
    if (edNum && edEnd > 1 && !RM) edNum.textContent = edFmt(1);
    let edDone = false;
    const printNumber = () => {
      if (!edNum || RM || edDone) return;
      edDone = true;
      if (edEnd < 1) { edNum.textContent = edTarget; return; }
      const dur = Math.min(2400, 900 + edEnd * 6);
      let t0 = 0, last = 0;
      const step = now => {
        if (!alive) return;
        /* the clock starts on the first painted frame, and a long stall (pin refresh, tab switch) doesn't skip the count */
        if (!t0) t0 = last = now;
        if (now - last > 100) t0 += now - last - 16;
        last = now;
        const k = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - k, 3);
        edNum.textContent = edFmt(Math.max(1, Math.round(1 + (edEnd - 1) * e)));
        if (k < 1) requestAnimationFrame(step); else edNum.textContent = edFmt(edEnd);
      };
      requestAnimationFrame(step);
    };

    /* ───────── record: find each line on the watch ───────── */
    const PARTS = {
      movement: { v: 'back', c: [736, 1000, 240] },
      case: { v: 'front', c: [736, 985, 660] },
      bezel: { v: 'front', c: [736, 975, 505], ring: 1 },
      dial: { v: 'front', c: [736, 973, 385] },
      glass: { v: 'back', c: [736, 1000, 296], ring: 1 },
      caseback: { v: 'back', c: [736, 1000, 600] },
      hands: { v: 'front', c: [748, 935, 255] },
      crown: { v: 'front', c: [1322, 1000, 120], ring: 1 },
      straps: { v: 'front', c: [736, 975, 1], rects: 1 },
      box: { v: 'box', c: [800, 1080, 1400] }
    };
    const rows = $$('.hk-row'), rvFrame = $('#hk-rvFrame'), rvCap = $('#hk-rvCap');
    const spC = $('#hk-spC'), spR = [$('#hk-spR1'), $('#hk-spR2')].filter(Boolean), spDim = $('#hk-spDim'), spRing = $('#hk-spRing');
    let partNow = '', hoverLock = false, scrollPart = '';
    const setPart = (key, row) => {
      if (!rvFrame || !key || key === partNow || !PARTS[key]) return; partNow = key;
      const p = PARTS[key];
      row = row || rows.find(r => r.dataset.part === key);
      rows.forEach(r => r.classList.toggle('hk-on', r === row));
      rvFrame.dataset.view = p.v;
      if (row) {
        rvCap.textContent = '';
        const s = document.createElement('span'), b = document.createElement('b');
        s.textContent = $('dt', row).textContent; b.textContent = $('dd', row).textContent;
        rvCap.append(s, b);
      }
      const [cx, cy, r] = p.c, dim = p.v === 'box' ? 0 : .74;
      if (G) {
        const d = RM ? 0 : 1;
        gsap.to(spC, { attr: { cx, cy, r }, duration: d, ease: 'expo.out', overwrite: true });
        gsap.to(spR, { attr: { opacity: p.rects ? 1 : 0 }, duration: d * .6, overwrite: true });
        gsap.to(spDim, { attr: { opacity: dim }, duration: d * .6, overwrite: true });
        gsap.to(spRing, { attr: { cx, cy, r: r + 26, opacity: p.ring ? .95 : 0 }, duration: d, ease: 'expo.out', overwrite: true });
      } else {
        spC.setAttribute('cx', cx); spC.setAttribute('cy', cy); spC.setAttribute('r', r);
        spR.forEach(x => x.setAttribute('opacity', p.rects ? 1 : 0));
        spDim.setAttribute('opacity', dim);
        spRing.setAttribute('cx', cx); spRing.setAttribute('cy', cy); spRing.setAttribute('r', r + 26); spRing.setAttribute('opacity', p.ring ? .95 : 0);
      }
    };
    if (FINE && rows.length) {
      rows.forEach(r => on(r, 'pointerenter', () => { hoverLock = true; partNow = ''; setPart(r.dataset.part, r); }));
      on($('#hk-rec'), 'pointerleave', () => { hoverLock = false; if (scrollPart) { partNow = ''; setPart(scrollPart.dataset.part, scrollPart); } });
    }
    const rowTrigger = (r, start, end) => ScrollTrigger.create({ trigger: r, start, end,
      onToggle: s => { if (s.isActive) { scrollPart = r; if (!hoverLock) { partNow = ''; setPart(r.dataset.part, r); } } } });

    /* ═════════════ scroll choreography ═════════════ */
    const heads = $$('.hk-split').map(el => ({ el, words: splitWords(el) }));
    const inks = $$('.hk-ink').map(el => splitWords(el, 'hk-iw')).flat();

    if (!G) {
      const first = rows.find(r => r.dataset.part === 'dial') || rows[0];
      first && setPart(first.dataset.part, first);
      return;
    }

    const railLinks = $$('.hk-rail a');
    const railTargets = railLinks.map(a => document.getElementById(a.getAttribute('href').slice(1)));
    const railOn = a => railLinks.forEach(x => x.classList.toggle('hk-on', x === a));
    railLinks.forEach((a, i) => {
      const sec = railTargets[i];
      if (!sec) return;
      const next = railTargets.slice(i + 1).find(Boolean);
      ScrollTrigger.create(next
        ? { trigger: sec, endTrigger: next, start: 'top 50%', end: 'top 50%', onToggle: s => s.isActive && railOn(a) }
        : { trigger: sec, start: 'top 50%', end: 'bottom 50%', onToggle: s => s.isActive && railOn(a) });
    });

    const stage2 = $('#hk-secondStage'), win = $('#hk-goldWindow'), ring = $('#hk-goldRing');

    if (RM) {
      if (win) gsap.set(win, { '--r': '80%' });
      if (stage2) stage2.classList.add('hk-gold');
      if (explode) { X.rx = 55; X.rz = -26; X.s = 1; renderExplode(); }
      gsap.set('.hk-tag', { opacity: 1 });
      rows.forEach(r => rowTrigger(r, 'top 60%', 'bottom 60%'));
      return;
    }

    mm = gsap.matchMedia();

    heads.forEach(({ el, words }) => {
      gsap.from(words, { yPercent: 112, duration: 1.25, ease: 'expo.out', stagger: .055, scrollTrigger: { trigger: el, start: 'top 88%' } });
    });

    /* hero: printed in two blocks. The Prussian key block goes down first, then the gold block
       is lowered onto it a touch out of register and settles into the kento. */
    if (hero && mark) {
      const heroTl = gsap.timeline({ delay: .15 });
      heroTl.from('#hk-markKey', { clipPath: 'inset(0 100% 0 0)', duration: 1.05, ease: 'power3.inOut' })
        .from(mark, { xPercent: 1.5, yPercent: -9, opacity: 0, duration: 1.1, ease: 'expo.out' }, .72)
        .from(markWrap, { scale: 1.012, duration: .5, ease: 'power2.out' }, .86)
        .from('#hk-heroWatch img', { y: 90, scale: .92, filter: 'blur(10px)', opacity: 0, duration: 1.7, ease: 'expo.out' }, .95)
        .from(heroName, { y: 16, opacity: 0, duration: 1.1, ease: 'expo.out' }, 1.3)
        .from('.hk-hero-foot > *, .hk-hero-top > *', { y: 26, opacity: 0, duration: 1.1, ease: 'expo.out', stagger: .08 }, 1.15);
      if (seal) heroTl.from(seal, { scale: 2.6, rotate: -34, opacity: 0, duration: .5, ease: 'power4.in' }, 1.55);
      const heroST = { trigger: hero, start: 'top top', end: 'bottom top', scrub: true };
      gsap.to('.hk-hw-par', { yPercent: -9, scale: 1.05, ease: 'none', scrollTrigger: heroST });
      gsap.to(mark, { y: () => -mark.offsetHeight * .2, x: () => -mark.offsetWidth * .012, ease: 'none', scrollTrigger: { ...heroST, invalidateOnRefresh: true } });
      gsap.to('#hk-markKey', { y: () => mark.offsetHeight * .12, x: () => mark.offsetWidth * .012, ease: 'none', scrollTrigger: { ...heroST, invalidateOnRefresh: true } });
      gsap.to('.hk-mark-stack', { yPercent: 30, opacity: .22, ease: 'none', scrollTrigger: heroST });
    }

    /* 02 · the print turns to gold */
    if (stage2 && win && ring) {
      const R = { r: 13 };
      const drawR = () => {
        win.style.setProperty('--r', R.r.toFixed(2) + '%');
        ring.style.setProperty('--r2', R.r.toFixed(2) + '%');
        ring.style.opacity = clamp(1 - (R.r - 40) / 25, 0, 1);
        stage2.classList.toggle('hk-gold', R.r > 42);
      };
      gsap.set(inks, { opacity: .14 });
      const sold = $('#hk-soldStamp');
      if (sold) ScrollTrigger.create({
        trigger: stage2, start: 'top 70%', once: true,
        onEnter: () => gsap.from('#hk-soldStamp b', { scale: 2.5, rotate: -30, opacity: 0, duration: .55, ease: 'power4.in', delay: .2, onComplete: () => gsap.fromTo(stage2, { x: -3 }, { x: 0, duration: .5, ease: 'elastic.out(1,.3)' }) })
      });
      mm.add('(min-width: 901px)', () => {
        const tl = gsap.timeline({ scrollTrigger: { trigger: '.hk-second-pin', start: 'center center', end: '+=150%', pin: true, scrub: 1 } });
        tl.to(inks, { opacity: 1, stagger: .02, duration: .3, ease: 'none' }, 0)
          .to(R, { r: 82, duration: 1, ease: 'power2.in', onUpdate: drawR }, .15);
        if (sold) tl.to(sold, { y: 30, opacity: 0, duration: .25 }, .7);
      });
      mm.add('(max-width: 900px)', () => {
        gsap.to(inks, { opacity: 1, stagger: .03, ease: 'none', scrollTrigger: { trigger: '.hk-second-copy', start: 'top 75%', end: 'bottom 55%', scrub: true } });
        /* on a phone the print turns to gold by itself once it is on screen, instead of waiting for the reader to scroll it away */
        const reveal = gsap.to(R, { r: 82, duration: 1.8, ease: 'power2.inOut', delay: .25, paused: true, onUpdate: drawR });
        ScrollTrigger.create({ trigger: stage2, start: 'top 70%', once: true, onEnter: () => reveal.play() });
      });
    }

    /* 02b · two gifts: the pieces are set down, then the seal is pressed across both */
    const giftPair = $('.hk-gift-pair');
    if (giftPair) {
      gsap.from('.hk-gifts-head .hk-side > *', { y: 30, opacity: 0, duration: 1.2, ease: 'expo.out', stagger: .08, scrollTrigger: { trigger: '.hk-gifts-head', start: 'top 80%' } });
      gsap.from('.hk-gift', { y: 80, opacity: 0, duration: 1.3, ease: 'expo.out', stagger: .14, scrollTrigger: { trigger: giftPair, start: 'top 86%' } });
      const gStamp = $('#hk-giftStamp');
      if (gStamp) {
        gsap.set(gStamp, { opacity: 0 });
        ScrollTrigger.create({
          trigger: giftPair, start: 'top 60%', once: true,
          onEnter: () => {
            gsap.set(gStamp, { opacity: 1 });
            gsap.fromTo('#hk-giftStamp b', { scale: 2.5, rotate: -30, opacity: 0 }, { scale: 1, rotate: -6, opacity: 1, duration: .55, ease: 'power4.in', delay: .35,
              onComplete: () => gsap.fromTo(giftPair, { x: -3 }, { x: 0, duration: .5, ease: 'elastic.out(1,.3)' }) });
            gsap.from('#hk-giftStamp small', { opacity: 0, y: 8, duration: .6, ease: 'expo.out', delay: .95 });
          }
        });
      }
    }

    /* 03 · the dial drifts as you pass it */
    if (dialStage && dialSec) {
      mm.add('(min-width: 901px)', () => {
        gsap.fromTo(dialStage, { y: -46 }, { y: 46, ease: 'none', scrollTrigger: { trigger: dialSec, start: 'top bottom', end: 'bottom top', scrub: 1 } });
      });
    }

    /* 03 · proofs: each sheet's new block is lowered a little out of register and pressed into the kento */
    const proofs = $$('.hk-proof');
    if (proofs.length) {
      const OFF = [{ x: 22, y: -16, r: -1.6 }, { x: -18, y: -20, r: 1.3 }, { x: 16, y: -22, r: -1.1 }];
      proofs.forEach(p => {
        gsap.set($$('.hk-blk', p), { opacity: 0 });
        gsap.set($$('.hk-proof-cap, .hk-n', p), { opacity: 0, y: 18 });
      });
      const printProof = (p, i, at, tl) => {
        const blks = $$('.hk-blk', p), fresh = blks[blks.length - 1], prior = blks.slice(0, -1), o = OFF[i % 3];
        if (prior.length) tl.to(prior, { opacity: 1, duration: .35, ease: 'power1.out' }, at);
        tl.fromTo(fresh, { x: o.x, y: o.y, rotation: o.r, scale: 1.035, opacity: 0 },
            { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1, duration: .85, ease: 'power3.out' }, at + (prior.length ? .28 : 0))
          .fromTo($('.hk-sheet', p), { scale: 1 }, { scale: .988, duration: .12, ease: 'power2.in', yoyo: true, repeat: 1 }, at + (prior.length ? .28 : 0) + .62)
          .to($$('.hk-proof-cap, .hk-n', p), { opacity: 1, y: 0, duration: .9, ease: 'expo.out', stagger: .06 }, at + .35);
      };
      /* on a phone connection the lazy plates can still be downloading when the sheet scrolls in:
         start fetching well ahead, and only press a sheet once its plates are decoded */
      const plates = p => $$('img', p);
      const warm = imgs => imgs.forEach(img => { img.loading = 'eager'; });
      const ready = imgs => Promise.race([
        Promise.all(imgs.map(img => (img.complete && img.naturalWidth ? Promise.resolve() : new Promise(r => { img.addEventListener('load', r, { once: true }); img.addEventListener('error', r, { once: true }); }))
          .then(() => img.decode ? img.decode().catch(() => {}) : null))),
        new Promise(r => setTimeout(r, 6000))
      ]);
      const row = $('.hk-proof-row');
      watch(new IntersectionObserver((es, o) => { if (es[0].isIntersecting) { warm(proofs.flatMap(plates)); o.disconnect(); } }, { rootMargin: '1200px 0px' })).observe(row);
      const press = (imgs, tl) => { warm(imgs); ready(imgs).then(() => { if (alive) tl.play(); }); };
      mm.add('(min-width: 901px)', () => {
        const tl = gsap.timeline({ paused: true });
        proofs.forEach((p, i) => printProof(p, i, i * .95, tl));
        ScrollTrigger.create({ trigger: row, start: 'top 72%', once: true, onEnter: () => press(proofs.flatMap(plates), tl) });
      });
      /* phones: the proofs are a swipe carousel, so each sheet is pressed when it is swiped into view
         (and only once the carousel itself has scrolled onto the screen) */
      mm.add('(max-width: 900px)', () => {
        const tls = proofs.map((p, i) => { const tl = gsap.timeline({ paused: true }); printProof(p, i, 0, tl); return tl; });
        const shown = new Set(), pressed = new Set();
        let rowSeen = false;
        const go = () => { if (rowSeen) proofs.forEach((p, i) => { if (shown.has(p) && !pressed.has(i)) { pressed.add(i); press(plates(p), tls[i]); } }); };
        const hio = new IntersectionObserver(es => { es.forEach(e => e.intersectionRatio >= .55 ? shown.add(e.target) : shown.delete(e.target)); go(); }, { root: row, threshold: [0, .55, 1] });
        proofs.forEach(p => hio.observe(p));
        ScrollTrigger.create({ trigger: row, start: 'top 78%', once: true, onEnter: () => { rowSeen = true; go(); } });
        return () => hio.disconnect();
      });
    }

    /* 03b · gold leaf drifts */
    if (leafSec) {
      if (leafGL) gsap.fromTo(leafGL.cam, { y: .44, z: 1.02 }, { y: .56, z: 1.14, ease: 'none', onUpdate: () => leafGL.draw(), scrollTrigger: { trigger: leafSec, start: 'top bottom', end: 'bottom top', scrub: true } });
      if ($('.hk-leaf-kanji')) gsap.from('.hk-leaf-kanji', { yPercent: 30, opacity: 0, duration: 1.4, ease: 'expo.out', scrollTrigger: { trigger: leafSec, start: 'top 70%' } });
    }

    /* 04 · straps */
    if (looks.length) gsap.from(looks, { y: 90, opacity: 0, duration: 1.4, ease: 'expo.out', stagger: .14, scrollTrigger: { trigger: '#hk-looks', start: 'top 84%' } });

    /* 05 · exploded caseback */
    if (explode) {
      const explodeTl = () => gsap.timeline()
        .to(X, { rx: 56, rz: -26, s: 1, duration: 1, ease: 'power2.inOut', onUpdate: renderExplode })
        .to('.hk-tag', { opacity: 1, stagger: .12, duration: .3 }, .7)
        .to(X, { rz: -38, duration: 1, ease: 'none', onUpdate: renderExplode }, 1);
      mm.add('(min-width: 901px)', () => {
        ScrollTrigger.create({ trigger: '#hk-deepPin', start: 'top top', end: '+=170%', pin: true, scrub: 1, animation: explodeTl(), onEnter: printNumber });
      });
      mm.add('(max-width: 900px)', () => {
        ScrollTrigger.create({ trigger: '#hk-deepStage', start: 'top 75%', end: 'bottom 25%', scrub: 1, animation: explodeTl() });
        if (edNum) ScrollTrigger.create({ trigger: '.hk-edition', start: 'top 85%', once: true, onEnter: printNumber });
      });
    }
    if ($('.hk-deep-scenes')) gsap.from('.hk-deep-scenes figure', { y: 70, opacity: 0, duration: 1.3, ease: 'expo.out', stagger: .14, scrollTrigger: { trigger: '.hk-deep-scenes', start: 'top 85%' } });

    /* 06 · the set is laid out on the table */
    if ($('#hk-setShot')) gsap.fromTo('#hk-setShot', { yPercent: 0 }, { yPercent: -10, ease: 'none', scrollTrigger: { trigger: '.hk-set-shot', start: 'top bottom', end: 'bottom top', scrub: true } });
    if ($('#hk-setBox')) gsap.fromTo('#hk-setBox', { y: 60, rotate: 1.5 }, { y: -40, rotate: -1, ease: 'none', scrollTrigger: { trigger: '.hk-set-stage', start: 'top bottom', end: 'bottom top', scrub: true } });
    if ($('.hk-set-note')) gsap.from('.hk-set-note', { y: 40, opacity: 0, duration: 1.2, ease: 'expo.out', scrollTrigger: { trigger: '.hk-set-note', start: 'top 88%' } });
    if (tilesEl) gsap.from('.hk-tile', { yPercent: 14, opacity: 0, duration: 1.2, ease: 'expo.out', stagger: .09, scrollTrigger: { trigger: tilesEl, start: 'top 86%' } });

    /* 07 · record rows light up as they cross the middle */
    rows.forEach(r => rowTrigger(r, 'top 58%', 'bottom 58%'));

    /* 08 · the views */
    const track = $('#hk-track'), bar = $('#hk-wearBar'), viewNo = $('#hk-viewNo');
    const views = track ? $$('.hk-view', track) : [];
    if (views.length) {
      const total = views.length;
      const label = n => String(n).padStart(2, '0');
      mm.add('(min-width: 901px)', () => {
        const last = views[views.length - 1];
        const dist = () => Math.max(0, last.offsetLeft + last.offsetWidth - innerWidth + innerWidth * .08);
        const tw = gsap.to([track, '#hk-wearIntro'], { x: () => -dist(), ease: 'none' });
        ScrollTrigger.create({
          trigger: '#hk-wearPin', start: 'top top', end: () => '+=' + dist(), pin: true, scrub: 1, animation: tw, invalidateOnRefresh: true,
          onUpdate: s => {
            if (bar) bar.style.transform = `scaleX(${s.progress.toFixed(3)})`;
            if (viewNo) viewNo.textContent = label(Math.min(total, 1 + Math.floor(s.progress * (total - .01))));
          }
        });
        views.forEach(v => gsap.fromTo($('img', v), { xPercent: -6 }, { xPercent: 6, ease: 'none', scrollTrigger: { trigger: v, containerAnimation: tw, start: 'left right', end: 'right left', scrub: true } }));
      });
      mm.add('(max-width: 900px)', () => {
        const onScroll = () => {
          const max = track.scrollWidth - track.clientWidth;
          const p = max > 0 ? track.scrollLeft / max : 0;
          if (bar) bar.style.transform = `scaleX(${p.toFixed(3)})`;
          if (viewNo) viewNo.textContent = label(Math.min(total, 1 + Math.round(p * (total - 1))));
        };
        track.addEventListener('scroll', onScroll, { passive: true });
        return () => track.removeEventListener('scroll', onScroll);
      });
    }
    if ($('.hk-final')) gsap.from('.hk-final-copy', { y: 40, opacity: 0, duration: 1.3, ease: 'expo.out', scrollTrigger: { trigger: '.hk-final', start: 'top 80%' } });

    /* refresh once fonts and images settle */
    ScrollTrigger.sort();
    const refresh = () => { if (!alive) return; fitMark(); ScrollTrigger.sort(); ScrollTrigger.refresh(); };
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
    if (document.readyState === 'complete') requestAnimationFrame(refresh); else on(window, 'load', refresh);
  };

  const ctx = G ? gsap.context(run) : (run(), null);

  return () => {
    alive = false;
    ac.abort();
    observers.forEach(o => o.disconnect());
    gls.forEach(g => g.destroy());
    if (mm) mm.revert();
    if (ctx) ctx.revert();
    if (lenis) { gsap.ticker.remove(lenis.__hkRaf); lenis.destroy(); lenis = null; }
  };
}

const loadScript = src => new Promise(res => {
  if (!src) { res(); return; }
  const s = document.createElement('script');
  s.src = src; s.async = false;
  s.onload = s.onerror = () => res();
  document.head.appendChild(s);
});
const wantsLenis = () => { const c = $('[data-hk-config]'); return !RM && !DESIGN && (!c || c.dataset.smooth !== 'false'); };
const ds = SELF ? SELF.dataset : {};
let teardown = () => {};
(async () => {
  if (!window.gsap) await loadScript(ds.gsap);
  if (!window.ScrollTrigger) await loadScript(ds.scrolltrigger);
  if (!window.Lenis && wantsLenis()) await loadScript(ds.lenis);
  teardown = boot();
})();

/* theme editor: rebuild after any section change so pins and triggers are measured against the new layout */
if (DESIGN) {
  let t = 0;
  const rebuild = () => { clearTimeout(t); t = setTimeout(() => { teardown(); teardown = boot(); }, 80); };
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(ev => document.addEventListener(ev, rebuild));
  document.addEventListener('shopify:section:select', e => { if (e.target.querySelector('#hk-drawer')) openDrawer(); });
  document.addEventListener('shopify:section:deselect', e => { if (e.target.querySelector('#hk-drawer')) closeDrawer(); });
}
})();
